import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
} from '@mui/material';
import { cancelSubscription, reinstateSubscription } from '../../services/esetApi';
import type { EsetCancelResult } from '../../services/esetApi';

/** Minimal identity the dialog needs. `cancelled` picks the mode: cancel vs (reinstate | remove). */
export interface EsetCancelTarget {
  id: string;
  customerName: string | null;
  productBundle: string | null;
  licenseRef: string | null;
  cancelled: boolean;
  removedFromVendor: string | null; // ISO date it was removed from ESET (then it can't be reinstated)
  trial: boolean; // ESET only lets a TRIAL licence be removed; a full one can only be suspended
  suspended: boolean;
}

interface EsetCancelDialogProps {
  sub: EsetCancelTarget | null; // null = closed
  onClose: () => void;
  /** Called after a REAL cancel / remove / reinstate, so the table can refresh. */
  onChanged?: () => void;
}

type EsetAction = 'leave' | 'suspend' | 'remove';

/** Cancel a subscription (manage stops billing it) and choose what happens in ESET. ESET's rules:
 *  a FULL licence can only be suspended (ESET stops billing it the next day) — that is its cancel;
 *  a TRIAL licence can also be removed permanently. A cancelled sub can be reinstated, or have its
 *  licence suspended / removed afterwards. Removal needs an explicit "can't be undone" tick.
 *  A cancel does NOT void a still-unpaid renewal invoice. */
export const EsetCancelDialog: React.FC<EsetCancelDialogProps> = ({ sub, onClose, onChanged }) => {
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [esetAction, setEsetAction] = useState<EsetAction>('leave');
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [preview, setPreview] = useState<EsetCancelResult | null>(null);
  const [result, setResult] = useState<EsetCancelResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const cancelledAlready = !!sub?.cancelled;
  const removed = !!sub?.removedFromVendor;

  useEffect(() => {
    if (!sub) return;
    let cancelled = false;
    setLoading(true);
    setEsetAction('leave');
    setConfirmRemove(false);
    setPreview(null);
    setResult(null);
    setError(null);
    const dry = sub.cancelled
      ? reinstateSubscription(sub.id, { dry_run: true })
      : cancelSubscription(sub.id, { dry_run: true });
    dry
      .then((res) => {
        if (!cancelled) setPreview(res);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load the subscription');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [sub]);

  if (!sub) return null;

  const run = async (fn: () => Promise<EsetCancelResult>) => {
    setRunning(true);
    setError(null);
    try {
      const res = await fn();
      setResult(res);
      if (['cancelled', 'removed', 'suspended', 'reinstated'].includes(res.status)) onChanged?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Action failed');
    } finally {
      setRunning(false);
    }
  };

  const doCancel = () =>
    run(() =>
      cancelSubscription(sub.id, {
        dry_run: false,
        suspend_license: esetAction === 'suspend',
        remove_license: esetAction === 'remove',
      }),
    );
  const doRemoveOnly = () => run(() => cancelSubscription(sub.id, { dry_run: false, remove_license: true }));
  const doSuspendOnly = () => run(() => cancelSubscription(sub.id, { dry_run: false, suspend_license: true }));
  // What a cancelled sub can still have done to it in ESET.
  const canRemoveLater = cancelledAlready && !removed && sub.trial;
  const canSuspendLater = cancelledAlready && !removed && !sub.trial && !sub.suspended;
  const doReinstate = () => run(() => reinstateSubscription(sub.id, { dry_run: false }));

  const busy = loading || running;
  const done = !!result && ['cancelled', 'removed', 'suspended', 'reinstated'].includes(result.status);
  const blocked = preview?.status === 'already_ended' || preview?.status === 'not_cancelled';
  const removeArmed = esetAction === 'remove' && confirmRemove;
  const title = cancelledAlready ? 'Cancelled subscription' : 'Cancel subscription';

  const removeWarning = (
    <Box sx={{ mt: 1 }}>
      <Alert severity="warning">
        Removing deletes licence <strong>{sub.licenseRef ?? ''}</strong> from the customer's ESET account
        for good — protection stops on their devices and ESET stops billing us for it. It can't be
        reinstated; re-selling means ordering a new licence.
      </Alert>
      <FormControlLabel
        sx={{ mt: 0.5 }}
        control={
          <Checkbox size="small" checked={confirmRemove} onChange={(e) => setConfirmRemove(e.target.checked)} />
        }
        label={<Box sx={{ fontSize: 13 }}>I understand this can't be undone</Box>}
      />
    </Box>
  );

  const resultText = (r: EsetCancelResult): string => {
    switch (r.status) {
      case 'cancelled':
        return r.remove_license
          ? 'Done — subscription cancelled and the licence removed from ESET.'
          : `Done — subscription cancelled${r.suspend_license ? ' and the ESET licence suspended' : ''}.`;
      case 'removed':
        return 'Done — the licence was removed from ESET.';
      case 'suspended':
        return 'Done — the licence was suspended in ESET; ESET stops billing it from tomorrow.';
      case 'reinstated':
        return 'Done — subscription reinstated.';
      default:
        return r.error ? `Failed: ${r.error}` : `No action: ${r.status}.`;
    }
  };

  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 0.5 }}>
        {title}
        <Box sx={{ fontSize: 13, fontWeight: 400, color: '#888', mt: 0.25 }}>
          {sub.customerName || '—'} · {sub.productBundle || '—'} · {sub.licenseRef || '—'}
        </Box>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : cancelledAlready ? (
          removed ? (
            <Box sx={{ fontSize: 13, color: '#555' }}>
              Cancelled, and the licence was removed from ESET on {sub.removedFromVendor}. It can't be
              reinstated — order a new licence to re-sell.
            </Box>
          ) : (
            <>
              <Box sx={{ fontSize: 13, color: '#555', mb: 1 }}>
                This subscription is cancelled — manage doesn't bill it.
                {sub.suspended && !sub.trial
                  ? ' The licence is suspended in ESET, so ESET no longer bills us for it.'
                  : ''}
              </Box>
              {canRemoveLater && (
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={esetAction === 'remove'}
                      onChange={(e) => {
                        setEsetAction(e.target.checked ? 'remove' : 'leave');
                        setConfirmRemove(false);
                      }}
                    />
                  }
                  label={<Box sx={{ fontSize: 13 }}>Remove the trial licence from ESET permanently</Box>}
                />
              )}
              {canSuspendLater && (
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={esetAction === 'suspend'}
                      onChange={(e) => setEsetAction(e.target.checked ? 'suspend' : 'leave')}
                    />
                  }
                  label={
                    <Box sx={{ fontSize: 13 }}>
                      Suspend the licence in ESET (ESET stops billing it from tomorrow)
                    </Box>
                  }
                />
              )}
              {esetAction === 'remove' && removeWarning}
            </>
          )
        ) : blocked ? (
          <Box sx={{ fontSize: 13, color: '#888', py: 3, textAlign: 'center' }}>
            This subscription has already ended ({preview?.current_status ?? 'ended'}).
          </Box>
        ) : (
          <>
            <Box sx={{ fontSize: 13, color: '#555', mb: 1 }}>
              Manage stops billing this subscription — no renewals, warnings or extra-seat charges. Its
              history is kept.
            </Box>
            <Box sx={{ fontSize: 12, fontWeight: 600, color: '#646464', mt: 1.5 }}>In ESET</Box>
            <RadioGroup
              value={esetAction}
              onChange={(e) => {
                setEsetAction(e.target.value as EsetAction);
                setConfirmRemove(false);
              }}
            >
              <FormControlLabel
                value="leave"
                control={<Radio size="small" />}
                label={<Box sx={{ fontSize: 13 }}>Leave as is</Box>}
              />
              <FormControlLabel
                value="suspend"
                control={<Radio size="small" />}
                label={
                  <Box sx={{ fontSize: 13 }}>
                    Suspend the licence — ESET stops billing it from tomorrow (can be unsuspended)
                  </Box>
                }
              />
              {sub.trial && (
                <FormControlLabel
                  value="remove"
                  control={<Radio size="small" />}
                  label={<Box sx={{ fontSize: 13 }}>Remove the trial licence permanently</Box>}
                />
              )}
            </RadioGroup>
            {!sub.trial && (
              <Box sx={{ fontSize: 12, color: '#888' }}>
                ESET doesn't allow a full licence to be removed — suspending it is how it's cancelled.
              </Box>
            )}
            {esetAction === 'remove' && removeWarning}
            {preview?.unpaid_invoice != null && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                Invoice {preview.unpaid_invoice} is still unpaid. Cancelling doesn't void it — use Void
                invoice if it shouldn't be paid.
              </Alert>
            )}
          </>
        )}

        {error && <Alert severity="error" sx={{ mt: 1.5 }}>{error}</Alert>}
        {result && (
          <Alert severity={done ? 'success' : 'warning'} sx={{ mt: 1.5 }}>
            {resultText(result)}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy} sx={{ color: '#aaa' }}>
          {done ? 'Close' : 'Back'}
        </Button>
        {!loading && !done && cancelledAlready && !removed && esetAction === 'remove' && (
          <Button variant="contained" color="error" onClick={doRemoveOnly} disabled={busy || !removeArmed}>
            {running ? 'Working…' : 'Remove from ESET'}
          </Button>
        )}
        {!loading && !done && canSuspendLater && esetAction === 'suspend' && (
          <Button variant="contained" color="error" onClick={doSuspendOnly} disabled={busy}>
            {running ? 'Working…' : 'Suspend in ESET'}
          </Button>
        )}
        {!loading && !done && cancelledAlready && !removed && esetAction === 'leave' && (
          <Button variant="contained" onClick={doReinstate} disabled={busy}>
            {running ? 'Working…' : 'Reinstate subscription'}
          </Button>
        )}
        {!loading && !done && !cancelledAlready && !blocked && (
          <Button
            variant="contained"
            color="error"
            onClick={doCancel}
            disabled={busy || (esetAction === 'remove' && !confirmRemove)}
          >
            {running ? 'Working…' : esetAction === 'remove' ? 'Cancel and remove' : 'Cancel subscription'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
